import yaml from 'js-yaml';
import { PlaybookEditState } from '../pages/labs/PlaybookEditPage';
import { parsePlaybookFromYaml } from './playbookUtils';

// Supported file extensions for import
const SUPPORTED_EXTENSIONS = ['json', 'yml', 'yaml'];

// Supported file names for import
const SUPPORTED_FILENAMES = ['playbook', 'connection'];

// Fields that indicate an incorrect file format (from reference)
const INVALID_FIELDS = [
  'writeKey',
  'integrations', 
  'connection',
  'endpointSchema',
  'integrationName',
  'pipelines'
];

export interface ImportValidationResult {
  isValid: boolean;
  errorMessage?: string;
  warningMessage?: string;
}

export interface ImportFileResult {
  fileName: string;
  fileType: 'playbook' | 'connection';
  data: any;
  validationResult: ImportValidationResult;
}

/**
 * Parse the content of an uploaded file based on its extension
 * Replicates the logic from get-data-from-file.js
 */
export const getDataFromFile = (extension: string, fileData: string): any => {
  let data;
  
  if (extension === 'yaml' || extension === 'yml') {
    data = yaml.load(fileData);
  } else if (extension === 'json') {
    data = JSON.parse(fileData);
  }

  return data;
};

/**
 * Validate an uploaded file against import rules
 * Replicates the logic from prevent-upload.js
 */
export const validateImportFile = (
  fileName: string, 
  extension: string, 
  parsedData: any, 
  playbookState?: PlaybookEditState
): ImportValidationResult => {
  // Check file extension
  if (!SUPPORTED_EXTENSIONS.includes(extension.toLowerCase())) {
    return {
      isValid: false,
      errorMessage: "This file type isn't supported. We only support JSON or YAML files. Please upload a file in one of these formats."
    };
  }

  // Check file name
  if (!SUPPORTED_FILENAMES.includes(fileName.toLowerCase())) {
    return {
      isValid: false,
      errorMessage: "Unsupported file name. Your file name must be playbook or connection to be processed. Please rename your file and try again."
    };
  }

  // Check for invalid format fields
  const hasInvalidFields = INVALID_FIELDS.some(field => parsedData[field] !== undefined);
  if (hasInvalidFields) {
    return {
      isValid: false,
      errorMessage: "This file doesn't match the expected format. Your file includes unexpected fields and can't be processed. Please check the required structure and try again."
    };
  }

  const { version } = parsedData;

  // Validate playbook files
  if (fileName.toLowerCase() === 'playbook') {
    if (!version) {
      return {
        isValid: false,
        errorMessage: "Version field is missing. Every playbook file needs a version field. Please add it and upload the file again."
      };
    }
  }

  // Validate connection files
  if (fileName.toLowerCase() === 'connection') {
    // Get connection type from playbookState if available
    const connectionType = playbookState?.connectionJSON ? 
      Object.keys(playbookState.connectionJSON)[0] : null;

    // SDK connection kits validation
    if (!version && connectionType === 'connectionTemplate') {
      return {
        isValid: false,
        errorMessage: "This SDK connection can't be edited. SDK connections of type connectionTemplate require a version field. If you need to update it, please check the documentation."
      };
    }

    if (!version && connectionType === 'endpointSchema') {
      // HTTP, make sure default field exists
      if (parsedData.default === undefined) {
        return {
          isValid: false,
          errorMessage: "Missing required fields in endpoint schema. Your file is missing the version and default fields, which are required for this type of connection. Please update the file and try again."
        };
      }
    }
  }

  // All validations passed
  return { isValid: true };
};

/**
 * Process an uploaded file and return the parsed result
 */
export const processImportFile = async (
  file: File, 
  playbookState?: PlaybookEditState
): Promise<ImportFileResult> => {
  try {
    // Extract file name and extension
    const fullFileName = file.name;
    const lastDotIndex = fullFileName.lastIndexOf('.');
    const fileName = lastDotIndex > 0 ? fullFileName.substring(0, lastDotIndex) : fullFileName;
    const extension = lastDotIndex > 0 ? fullFileName.substring(lastDotIndex + 1) : '';

    // Read file content
    const fileContent = await readFileAsText(file);

    // Parse file data
    const parsedData = getDataFromFile(extension, fileContent);

    // Validate the file
    const validationResult = validateImportFile(fileName, extension, parsedData, playbookState);

    // Determine file type
    const fileType = fileName.toLowerCase() === 'playbook' ? 'playbook' : 'connection';

    return {
      fileName,
      fileType,
      data: parsedData,
      validationResult
    };
  } catch (error) {
    return {
      fileName: file.name,
      fileType: 'playbook', // Default
      data: null,
      validationResult: {
        isValid: false,
        errorMessage: `Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`
      }
    };
  }
};

/**
 * Read a file as text content
 */
const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error('Failed to read file content'));
      }
    };
    reader.onerror = () => reject(new Error('File reading failed'));
    reader.readAsText(file);
  });
};

/**
 * Apply imported playbook data to the current playbook state
 */
export const applyImportedPlaybookData = (
  currentState: PlaybookEditState,
  importedData: any
): Partial<PlaybookEditState> => {
  try {
    // Parse the imported YAML into a Playbook structure
    const parsedPlaybook = parsePlaybookFromYaml(yaml.dump(importedData));

    // Convert "Default transforms" from imported playbook into Event-specific transforms
    // This is part of the 2.0 experience where default transforms become omnipresent event-specific transforms
    const updatedTransforms = [...parsedPlaybook.uiTransforms];
    const updatedEventConfigs = [...parsedPlaybook.uiEventConfigs];

    // Find any transforms that were originally in the "default" section
    const defaultTransforms = updatedTransforms.filter(transform => {
      return transform.__yamlMetadata?.section === 'default';
    });

    // If there are default transforms, convert them to be omnipresent across all events
    if (defaultTransforms.length > 0) {
      // Update these transforms to apply to all existing events
      const allEventIds = updatedEventConfigs.map(event => event.id);
      
      defaultTransforms.forEach(transform => {
        // Make this transform apply to all events
        transform.eventIds = allEventIds;
        transform.appliesToScope = 'eventSpecific';
        
        // Update the metadata to reflect the conversion
        if (transform.__yamlMetadata) {
          transform.__yamlMetadata.section = 'eventSpecific';
        }
      });

      // Add these transforms to all event configs
      updatedEventConfigs.forEach(eventConfig => {
        if (eventConfig.transformItemIds) {
          defaultTransforms.forEach(transform => {
            if (!eventConfig.transformItemIds!.includes(transform.id)) {
              eventConfig.transformItemIds!.push(transform.id);
            }
          });
        }
      });
    }

    return {
      name: importedData.name || currentState.name,
      description: importedData.description || currentState.description,
      version: importedData.version || currentState.version,
      params: importedData.params || currentState.params,
      input_schema_id: importedData.input_schema_id || currentState.input_schema_id,
      output_schema_id: importedData.output_schema_id || currentState.output_schema_id,
      uiTransforms: updatedTransforms,
      uiEventConfigs: updatedEventConfigs,
      autoPromoteGlobals: importedData.autoPromoteGlobals ?? currentState.autoPromoteGlobals,
      allowUndefinedTransforms: importedData.allowUndefinedTransforms ?? currentState.allowUndefinedTransforms,
      __originalYaml: importedData, // Store the original imported YAML
    };
  } catch (error) {
    throw new Error(`Failed to apply imported playbook data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Apply imported connection data to the current playbook state
 */
export const applyImportedConnectionData = (
  importedData: any // This is already parsed YAML data
): Partial<PlaybookEditState> => {
  try {
    // Create a proper ConnectionConfig from the imported data
    const connectionConfig = {
      params: importedData.params || [],
      default: importedData.default || {
        endpoint: '',
        method: 'POST',
        headers: []
      },
      eventSpecific: importedData.eventSpecific
    };

    return {
      connectionJSON: connectionConfig,
    };
  } catch (error) {
    throw new Error(`Failed to apply imported connection data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Check if a file is a supported import file type
 */
export const isSupportedImportFile = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  // For now, let's be more lenient and just check the extension
  return SUPPORTED_EXTENSIONS.includes(extension);
};

/**
 * Extract multiple files from a drag and drop event
 */
export const extractFilesFromDragEvent = (event: DragEvent): File[] => {
  const files: File[] = [];
  
  if (event.dataTransfer?.files) {
    Array.from(event.dataTransfer.files).forEach(file => {
      if (isSupportedImportFile(file)) {
        files.push(file);
      }
    });
  }
  
  return files;
};
